import { useAuth } from 'react-oidc-context';

/**
 * The admin role tiers. MUST mirror the backend `auth/role-hierarchy.ts`
 * (admin ⊇ operator ⊇ viewer) so the SPA gates a route exactly as the service
 * would — the service is still the source of truth (it re-checks every call),
 * the SPA gate is UX only.
 */
export type AdminRole = 'admin' | 'operator' | 'viewer';

const RANK: Record<AdminRole, number> = { admin: 3, operator: 2, viewer: 1 };

function isAdminRole(value: unknown): value is AdminRole {
  return value === 'admin' || value === 'operator' || value === 'viewer';
}

function base64UrlDecode(segment: string): string {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
  return atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, '='));
}

/**
 * Read the flat `roles` claim from the ACCESS token (the same claim the service
 * verifier reads — admin-shell-oidc-rbac B4). The Keycloak default
 * `realm_access.roles` shape is intentionally ignored: a mapper must emit a flat
 * `roles: string[]` or both the service AND this SPA see no roles.
 */
export function decodeRoles(accessToken: string | undefined): AdminRole[] {
  if (!accessToken) {
    return [];
  }
  const parts = accessToken.split('.');
  if (parts.length < 2) {
    return [];
  }
  try {
    const payload = JSON.parse(base64UrlDecode(parts[1]!)) as { roles?: unknown };
    return Array.isArray(payload.roles) ? payload.roles.filter(isAdminRole) : [];
  } catch {
    return [];
  }
}

/** True if any held role satisfies the required tier under the hierarchy. */
export function satisfies(held: readonly AdminRole[], required: AdminRole): boolean {
  return held.some((role) => RANK[role] >= RANK[required]);
}

/** True if the held roles satisfy ANY of the required tiers (any-of + hierarchy). */
export function satisfiesAny(held: readonly AdminRole[], required: readonly AdminRole[]): boolean {
  return required.some((tier) => satisfies(held, tier));
}

/** Hook: the current user's admin roles, decoded from the access token. */
export function useRoles(): AdminRole[] {
  const auth = useAuth();
  return decodeRoles(auth.user?.access_token);
}
